import React from "react";

/**
 * Section-by-section scroll snapping that respects tall sections.
 *
 * Behaviour:
 *  - Sections shorter than or equal to the viewport snap immediately on a
 *    single wheel/swipe/key press.
 *  - Sections taller than the viewport (e.g. mobile "What We Do" with three
 *    stacked cards) allow native scrolling first; only the swipe/wheel/key
 *    issued AT the edge of the section actually jumps to the neighbour.
 *  - Interactive elements (form inputs, listboxes, etc.) are exempt so the
 *    contact form stays usable.
 */
export const useSectionScrollSnap = (sectionRefs) => {
  React.useEffect(() => {
    const sections = sectionRefs.map((r) => r.current).filter(Boolean);
    if (sections.length === 0) return undefined;

    let isAnimating = false;
    let lastTrigger = 0;
    const COOLDOWN = 950; // ms — matches smooth-scroll duration
    const EDGE_TOL = 16; // px tolerance for "at edge of section"

    const getCurrentIndex = () => {
      const y = window.scrollY + window.innerHeight / 2;
      let idx = 0;
      sections.forEach((s, i) => {
        if (s.offsetTop <= y) idx = i;
      });
      return idx;
    };

    const sectionEdges = (section) => {
      const top = section.offsetTop;
      const bottom = top + section.offsetHeight;
      const viewportTop = window.scrollY;
      const viewportBottom = viewportTop + window.innerHeight;
      return {
        remainingBelow: bottom - viewportBottom,
        scrolledFromTop: viewportTop - top,
      };
    };

    const goTo = (idx) => {
      if (idx < 0 || idx >= sections.length) return;
      isAnimating = true;
      lastTrigger = Date.now();
      sections[idx].scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => {
        isAnimating = false;
      }, COOLDOWN);
    };

    const isInteractive = (el) => {
      if (!el || typeof el.closest !== "function") return false;
      return !!el.closest(
        'form, input, textarea, select, [role="listbox"], [data-radix-popper-content-wrapper]'
      );
    };

    /** Returns true and performs a snap, false if native scroll should run. */
    const trySnap = (direction) => {
      const now = Date.now();
      if (isAnimating || now - lastTrigger < COOLDOWN) return true; // swallow
      const idx = getCurrentIndex();
      const section = sections[idx];
      if (!section) return false;
      const { remainingBelow, scrolledFromTop } = sectionEdges(section);
      if (direction === 1 && remainingBelow > EDGE_TOL) {
        return false; // tall section — let native scroll handle it
      }
      if (direction === -1 && scrolledFromTop > EDGE_TOL) {
        return false; // scrolled past top of section — let native scroll up
      }
      goTo(idx + direction);
      return true;
    };

    const onWheel = (e) => {
      if (isInteractive(e.target)) return;
      if (Math.abs(e.deltaY) < 8) return;
      const direction = e.deltaY > 0 ? 1 : -1;
      if (trySnap(direction)) {
        e.preventDefault();
      }
    };

    const onKey = (e) => {
      if (isInteractive(e.target)) return;
      const down = ["ArrowDown", "PageDown", " ", "Spacebar"].includes(e.key);
      const up = ["ArrowUp", "PageUp"].includes(e.key);
      if (!down && !up) return;
      const direction = down ? 1 : -1;
      if (trySnap(direction)) {
        e.preventDefault();
      }
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("keydown", onKey);

    // Touch — one swipe at the section edge jumps to the neighbour. Inside a
    // tall section, swipes scroll natively (touchstart is passive).
    let touchStartY = null;
    const onTouchStart = (e) => {
      if (e.touches.length !== 1 || isInteractive(e.target)) {
        touchStartY = null;
        return;
      }
      touchStartY = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
      if (touchStartY == null) return;
      const endTouch = e.changedTouches && e.changedTouches[0];
      if (!endTouch) return;
      const dy = touchStartY - endTouch.clientY; // positive = swipe up = scroll down
      touchStartY = null;
      if (Math.abs(dy) < 40) return;
      trySnap(dy > 0 ? 1 : -1);
    };
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [sectionRefs]);
};

/**
 * Track which named section is currently in view (>=55% visible) to drive
 * the nav-link underline.
 */
export const useActiveSection = (entries) => {
  const [activeSection, setActiveSection] = React.useState(entries[0]?.id);

  React.useEffect(() => {
    const observers = entries.map(({ id, ref }) => {
      const el = ref.current;
      if (!el) return null;
      // Sensor band: the 10% strip at the vertical centre of the viewport.
      // Whichever section's body crosses this band is "active" — works
      // reliably regardless of whether a section is taller than the viewport.
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id);
        },
        { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((o) => o && o.disconnect());
  }, [entries]);

  return activeSection;
};
